import type {
  Announcement,
  BoardView,
  CaptureTally,
  ClientMessage,
  Color,
  ErrorCode,
  GameStatus,
  Mode,
  PromotionType,
  ServerMessage,
  Square,
  EndReason,
} from '@blind-chess/shared';

interface GameStateValue {
  ws: WebSocket | null;
  connected: boolean;
  gameId: string | null;
  you: Color | null;
  view: BoardView | null;
  announcements: Announcement[];
  gameStatus: GameStatus;
  mode: Mode | null;
  highlightingEnabled: boolean;
  touchedPiece: Square | null;
  drawOffer: { from: Color } | null;
  endReason: EndReason | null;
  winner: Color | null;
  opponentConnected: boolean;
  lastError: { code: ErrorCode; message: string; at: number } | null;
  aiOpponent: { color: Color; brain: 'casual' | 'recon' } | null;
  captures: CaptureTally;
}

function makeStore() {
  const state = $state<GameStateValue>({
    ws: null,
    connected: false,
    gameId: null,
    you: null,
    view: null,
    announcements: [],
    gameStatus: 'waiting',
    mode: null,
    highlightingEnabled: false,
    touchedPiece: null,
    drawOffer: null,
    endReason: null,
    winner: null,
    opponentConnected: false,
    lastError: null,
    aiOpponent: null,
    captures: { byYou: {}, byOpponent: {} },
  });

  function tokenKey(gameId: string) { return `bc:${gameId}`; }

  function connect(gameId: string, joinAs?: Color | 'auto') {
    if (state.ws) state.ws.close();
    state.gameId = gameId;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${proto}://${location.host}/ws?game=${gameId}`;
    const ws = new WebSocket(url);
    state.ws = ws;
    ws.onopen = () => {
      state.connected = true;
      const token = localStorage.getItem(tokenKey(gameId)) ?? undefined;
      const hello: ClientMessage = token
        ? { type: 'hello', gameId, token }
        : { type: 'hello', gameId, joinAs: joinAs ?? 'auto' };
      ws.send(JSON.stringify(hello));
    };
    ws.onmessage = (ev) => onServerMessage(JSON.parse(ev.data) as ServerMessage);
    ws.onclose = () => {
      state.connected = false;
      // attempt reconnect after 2s if game is still active
      if (state.gameStatus === 'active') {
        setTimeout(() => { if (state.gameId === gameId) connect(gameId); }, 2000);
      }
    };
    ws.onerror = () => { state.connected = false; };
  }

  function onServerMessage(m: ServerMessage) {
    switch (m.type) {
      case 'joined':
        if (m.you === 'spectator-rejected') {
          state.lastError = { code: 'spectators_disabled', message: 'both slots filled', at: Date.now() };
          return;
        }
        state.you = m.you;
        state.view = m.view;
        state.announcements = m.announcements;
        state.gameStatus = m.gameStatus;
        state.mode = m.mode;
        state.highlightingEnabled = m.highlightingEnabled;
        state.opponentConnected = m.opponentConnected;
        state.aiOpponent = m.aiOpponent ?? null;
        state.captures = m.captures;
        if (state.gameId) localStorage.setItem(tokenKey(state.gameId), m.token);
        break;
      case 'update':
        state.view = m.view;
        state.gameStatus = m.gameStatus;
        state.touchedPiece = m.touchedPiece ?? null;
        state.drawOffer = m.drawOffer ?? null;
        state.endReason = m.endReason ?? null;
        state.winner = m.winner ?? null;
        if (m.newAnnouncements.length) {
          state.announcements = [...state.announcements, ...m.newAnnouncements];
        }
        if (m.aiOpponent !== undefined) state.aiOpponent = m.aiOpponent;
        state.captures = m.captures;
        break;
      case 'peer-status':
        if (state.you && m.color !== state.you) {
          state.opponentConnected = m.connected;
        }
        break;
      case 'error':
        state.lastError = { code: m.code, message: m.message, at: Date.now() };
        break;
      case 'ping':
        send({ type: 'pong' });
        break;
    }
  }

  function send(m: ClientMessage) {
    if (state.ws?.readyState === WebSocket.OPEN) {
      state.ws.send(JSON.stringify(m));
    }
  }

  function commit(from: Square, to?: Square, promotion?: PromotionType) {
    send({ type: 'commit', from, to, promotion });
  }

  function resign() { send({ type: 'resign' }); }
  function offerDraw() { send({ type: 'offer-draw' }); }
  function respondDraw(accept: boolean) { send({ type: 'respond-draw', accept }); }

  function disconnect() {
    state.ws?.close();
    state.ws = null;
    state.connected = false;
  }

  return {
    state,
    connect,
    disconnect,
    commit,
    resign,
    offerDraw,
    respondDraw,
  };
}

export const game = makeStore();
